import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { forgotPassword } from "./../../services/passwordServices";
import backgroundImage from "./HeaderSlider_02.jpg";

const BRAND = {
  blue: "#013E8E",
  green: "#4CA82F",
  blueLight: "#038DE6",
};

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const emailOk = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    [email],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const cleanEmail = email.trim().toLowerCase();
      const result = await forgotPassword(cleanEmail);

      if (result?.success) {
        setMessage(
          "Si un compte avec cet email existe, un lien de réinitialisation a été envoyé.",
        );
        setTimeout(() => navigate("/login"), 2500);
      } else {
        setErrorMessage(result?.message || "Impossible d'envoyer le lien.");
      }
    } catch (e) {
      setErrorMessage("Impossible d'envoyer le lien pour le moment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center px-4 py-10"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* overlay */}
      <div className="absolute inset-0 bg-black/55" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(circle at top, rgba(1,62,142,0.55), transparent 55%), radial-gradient(circle at bottom, rgba(76,168,47,0.55), transparent 55%)",
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="bg-white/50 backdrop-blur-md border border-white/50 shadow-2xl rounded-2xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src="/img/logos/logo_monappliclub.png"
              alt="MonAppliClub"
              className="h-14 md:h-16 mx-auto mb-3 select-none"
              draggable="false"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
              Mot de passe oublié
            </h1>
            <p className="text-sm text-slate-700 mt-2">
              Recevez un lien de réinitialisation par email
            </p>
          </div>

          {/* Messages */}
          {message && (
            <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800 text-sm">
              {message}
            </div>
          )}
          {errorMessage && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Email
              </label>
              <input
                type="email"
                placeholder="Entrez votre email"
                className={`w-full rounded-xl border bg-white px-4 py-3 outline-none transition
                  ${email.length && !emailOk ? "border-rose-300" : "border-slate-200"}
                  focus:ring-2 focus:ring-[#013E8E]/25 focus:border-[#013E8E]
                `}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              {email.length > 0 && !emailOk && (
                <p className="mt-1 text-xs text-rose-700">Email invalide.</p>
              )}
            </div>

            {/* CTA */}
            <button
              type="submit"
              disabled={isSubmitting || !emailOk}
              className={`w-full rounded-xl px-4 py-3 font-semibold shadow-sm transition border
                ${isSubmitting || !emailOk ? "cursor-not-allowed opacity-80" : "hover:opacity-95"}
              `}
              style={{
                borderColor: BRAND.blue,
                color: "white",
                backgroundImage: `linear-gradient(90deg, ${BRAND.blue}, ${BRAND.green})`,
              }}
            >
              {isSubmitting ? "Envoi..." : "Envoyer le lien"}
            </button>

            {/* Info box */}
            <div className="rounded-xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-700">
              Merci de vérifier également vos <strong>spams</strong> /{" "}
              <strong>courriers indésirables</strong>.
            </div>

            <div className="flex items-center justify-between pt-2 text-sm">
              <Link
                to="/login"
                className="hover:underline"
                style={{ color: BRAND.blue }}
              >
                Retour connexion
              </Link>
              <Link to="/register" className="text-slate-700 hover:underline">
                Créer un compte
              </Link>
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-white/85 mt-4">
          MonAppliClub • Réinitialisation sécurisée
        </p>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
